import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import type { BankAccount } from 'api/types';

export interface BankAccountViewModalProps {
  isOpen: boolean;
  bankAccount: BankAccount | null;
  onClose: () => void;
  onEdit?: (item: BankAccount) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (id: string) => void;
}

const ReadOnlyField: React.FC<{ label: string; value?: string | number | boolean | null; span?: boolean }> = ({ label, value, span }) => (
  <div style={span ? { gridColumn: '1 / -1' } : undefined}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
      {value != null && value !== '' ? (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)) : '-'}
    </div>
  </div>
);

const BankAccountViewModal: React.FC<BankAccountViewModalProps> = ({
  isOpen, bankAccount, onClose, onEdit, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !bankAccount) return;
    minimize({
      id: `${tabId}-view-modal-${bankAccount.id}`,
      title: `View - ${bankAccount.accountCode}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(bankAccount.id),
    });
    onClose();
  }, [tabId, moduleName, bankAccount, minimize, onClose, onRestoreView]);

  if (!isOpen || !bankAccount) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">Bank Account Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
            <ReadOnlyField label="Account Code" value={bankAccount.accountCode} />
            <ReadOnlyField label="Account Name" value={bankAccount.accountName} />
            <div>
              <label className="form-label">Status</label>
              <div>
                <span className={`badge badge-${bankAccount.isActive ? 'active' : 'inactive'}`}>
                  {bankAccount.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Bank Details</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Bank Name" value={bankAccount.bankName} />
              <ReadOnlyField label="Branch" value={bankAccount.branchName} />
              <ReadOnlyField label="Account Type" value={bankAccount.accountType} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <ReadOnlyField label="Account Number" value={bankAccount.accountNumber} />
              <ReadOnlyField label="IFSC Code" value={bankAccount.ifscCode} />
              <ReadOnlyField label="SWIFT Code" value={bankAccount.swiftCode} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <ReadOnlyField label="UPI ID" value={bankAccount.upiId} />
              <ReadOnlyField label="Currency" value={bankAccount.currency} />
            </div>
          </div>

          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Property Mapping</h3>
            <ReadOnlyField label="Mapped Properties" value={bankAccount.mappedProperties} span />
          </div>

          {bankAccount.notes && (
            <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Notes</h3>
              <div style={{ padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                {bankAccount.notes}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(bankAccount); }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default BankAccountViewModal;
